import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import { formRegistry, FormName } from './formRegistry';

type ZustandHook<State> = {
    <T>(selector: (state: State) => T, equalityFn?: (a: T, b: T) => boolean): T;
    getState: () => State;
};

export function useDynamicField(formName: FormName, field: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const useStore = formRegistry[formName] as unknown as ZustandHook<any>;

    const { value, error, touched } = useStore(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        useShallow((s: any) => ({
            value: s.values[field],
            error: s.errors[field],
            touched: s.touched[field],
        }))
    );

    const onChange = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (newValue: any, debounce?: boolean) => {
            const s = useStore.getState();
            const currentValue = s.values[field];
            const isAutoComplete = !currentValue && +newValue < 1_000_00_00_000;
            const withoutCountryPrefix = !!+newValue && +newValue > 9_999_99_99_9;

            if (isAutoComplete && withoutCountryPrefix) {
                s.updateField(field, `+1${+newValue}`, debounce);
                return;
            }

            s.updateField(field, newValue, debounce);
        },
        [useStore, field]
    );

    const onBlur = useCallback(() => {
        useStore.getState().touchField(field);
    }, [useStore, field]);

    return { value, error, touched, onChange, onBlur };
}
